export type FrameTicker = {
    /** Stop ticking and release the worker. Safe to call more than once. */
    stop: () => void
    /** 'worker' while a Web Worker drives the ticks, 'interval' for the main-thread fallback. */
    readonly source: 'worker' | 'interval'
}

/**
 * The worker only keeps time: an interval that posts a tick. Browsers throttle a hidden tab's main-thread timers to
 * about once a second, but not a worker's, so ticks keep arriving at the requested rate while the tab is in the
 * background. Loaded from a Blob URL (like the app's audio worklets), so no bundler setup is needed.
 */
const WORKER_SOURCE = `
let timer = null
self.onmessage = (event) => {
    const message = event.data
    if (message && message.type === 'start') {
        clearInterval(timer)
        timer = setInterval(() => self.postMessage('tick'), message.intervalMs)
    } else if (message && message.type === 'stop') {
        clearInterval(timer)
        timer = null
    }
}
`

type WorkerHandle = { stop: () => void }

/** Start the worker timer; null if it can't be created. `onFail` fires if it breaks later (e.g. its script won't load). */
function startWorkerTicker(intervalMs: number, onTick: () => void, onFail: () => void): WorkerHandle | null {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL?.createObjectURL !== 'function') {
        return null
    }
    let url: string | null = null
    try {
        url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'application/javascript' }))
        const worker = new Worker(url)
        const workerUrl = url
        let stopped = false
        const stop = () => {
            if (stopped) return
            stopped = true
            worker.postMessage({ type: 'stop' })
            worker.terminate()
            URL.revokeObjectURL(workerUrl)
        }
        worker.onmessage = () => {
            if (!stopped) onTick()
        }
        // Loading or running the worker can fail after construction; without a fallback the recording would freeze.
        worker.onerror = () => {
            if (stopped) return
            stop()
            onFail()
        }
        worker.postMessage({ type: 'start', intervalMs })
        return { stop }
    } catch {
        if (url) URL.revokeObjectURL(url)
        return null
    }
}

/**
 * Calls `onTick` about `fps` times a second — from a Web Worker timer when available (keeps its rate in a hidden tab),
 * otherwise, or if the worker fails at any point, from a main-thread `setInterval` (the previous behaviour).
 */
export function createFrameTicker(fps: number, onTick: () => void): FrameTicker {
    const intervalMs = 1000 / fps
    let stopped = false
    let intervalId: ReturnType<typeof setInterval> | null = null
    let source: FrameTicker['source'] = 'worker'

    const startInterval = () => {
        if (stopped || intervalId !== null) return
        source = 'interval'
        intervalId = setInterval(onTick, intervalMs)
    }

    const worker = startWorkerTicker(intervalMs, onTick, startInterval)
    if (!worker) startInterval()

    return {
        get source() {
            return source
        },
        stop() {
            if (stopped) return
            stopped = true
            worker?.stop()
            if (intervalId !== null) clearInterval(intervalId)
            intervalId = null
        },
    }
}
